// Queued files kept in IndexedDB, for the web and mobile apps. Browsers keep a stored Blob on
// disk, so saving a large file does not hold it in memory, and reading it back goes through
// Blob.slice one part at a time.

import { blobSource } from "./chunked";
import type { FileSpool } from "./uploadQueue";

const STORE = "files";

export class IdbFileSpool implements FileSpool {
  private db: Promise<IDBDatabase> | null = null;

  /** One database per signed-in account, like the message store. */
  constructor(
    private readonly name: string,
    private readonly idb: IDBFactory = indexedDB,
  ) {}

  private connect(): Promise<IDBDatabase> {
    this.db ??= new Promise((resolve, reject) => {
      const req = this.idb.open(this.name, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this.db;
  }

  private async run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
    const db = await this.connect();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      let result: T;
      req.onsuccess = () => {
        result = req.result as T;
      };
      // A write only counts once its transaction has committed.
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error ?? req.error);
      tx.onabort = () => reject(tx.error ?? new Error("The file could not be saved."));
    });
  }

  async put(id: string, file: Blob): Promise<boolean> {
    try {
      // Stored as a plain Blob, so a File's name and date need no special handling.
      const blob = file instanceof File ? new Blob([file], { type: file.type }) : file;
      await this.run("readwrite", (s) => s.put(blob, id));
      return true;
    } catch {
      // Usually QuotaExceededError. The upload goes on from memory for this session.
      return false;
    }
  }

  async open(id: string) {
    try {
      const blob = await this.run<Blob | undefined>("readonly", (s) => s.get(id));
      return blob ? blobSource(blob) : null;
    } catch {
      return null;
    }
  }

  async delete(id: string): Promise<void> {
    await this.run("readwrite", (s) => s.delete(id));
  }

  async ids(): Promise<string[]> {
    return (await this.run<IDBValidKey[]>("readonly", (s) => s.getAllKeys())).map(String);
  }
}
